import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import styles from './InfoList.module.scss';

export type InfoListData = Record<string, string | number | JSX.Element | null | undefined>;

interface InfoListProps<T> {
	data: InfoListData;
	translations?: T;
	wrapperClassName?: string;
	dlClassName?: string;
	groupClassName?: string;
	noPadding?: boolean;
	title?: string;
	text?: string;
	listOnly?: boolean;
	numberOfColumns?: number;
	filterEmptyValues?: boolean;
}

export function InfoList<T>({
	data,
	translations,
	wrapperClassName,
	dlClassName,
	groupClassName,
	title,
	text,
	noPadding,
	listOnly,
	numberOfColumns = 1,
	filterEmptyValues,
}: InfoListProps<T>) {
	const columns = useMemo(() => {
		return Object.keys(data)?.length > 2 ? numberOfColumns : 1;
	}, [numberOfColumns, data]);

	const renderList = useCallback(() => {
		const keys = Object.keys(data);
		return (
			<dl className={classNames(styles.dl, dlClassName)} style={{ '--columns': columns } as React.CSSProperties}>
				{keys.map((key) => {
					const value = data[key];

					if ((value == null || value === '') && filterEmptyValues) {
						return null;
					}

					return (
						<div
							key={key}
							className={classNames(styles.group, groupClassName)}
							style={{ '--columns': columns } as React.CSSProperties}
						>
							<dt className={styles.dt}>{translations?.[key] || key}</dt>
							<dd className={styles.dd}>{value}</dd>
						</div>
					);
				})}
			</dl>
		);
	}, [data, dlClassName, translations, groupClassName, columns, filterEmptyValues]);

	return listOnly ? (
		renderList()
	) : (
		<section className={classNames(styles.wrapper, wrapperClassName, { [styles.noPadding]: noPadding })}>
			{title && <p className={styles.title}>{title}</p>}
			{text && <p className={styles.text}>{text}</p>}
			{renderList()}
		</section>
	);
}
